import { createSlice } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';

import { TRAINING_STRUCTURES } from 'constants/common';

import { applyBulkLessonStatusToCourseLikeState } from 'store/helpers/applyBulkLessonStatusToCourseLikeState';
import { removeLessonById, updateLessonStatusById } from 'store/lesson/actions';

import {
  getTrainingById,
  updateTrainingById,
  createTrainingModule,
  updateTrainingModuleById,
  removeTrainingModuleById,
  reorderTrainingLessons,
  reorderTrainingModules,
  updateTrainingStatus,
} from './actions';

const trainingSlice = createSlice({
  name: 'currentTraining',
  initialState: null,
  reducers: {},
  extraReducers: (builder: any) => {
    builder.addCase(getTrainingById.fulfilled, (_: any, action: any) => {
      return action.payload;
    });

    builder.addCase(updateTrainingById.fulfilled, (state: any, action: any) => {
      return {
        ...state,
        ...action.payload,
      };
    });

    builder.addCase(
      createTrainingModule.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: [
            ...(state.modules || []),
            { ...action.payload, lessons: [] },
          ],
        };
      },
    );

    builder.addCase(
      updateTrainingModuleById.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: state.modules.map((module: any) => {
            if (module.id === action.payload.id) {
              return {
                ...module,
                ...action.payload,
              };
            }

            return module;
          }),
        };
      },
    );

    builder.addCase(
      removeTrainingModuleById.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: state.modules.filter(
            (module: any) => module.id !== action.payload.id,
          ),
        };
      },
    );

    builder.addCase(
      updateTrainingStatus.fulfilled,
      (state: any, action: any) => {
        const { bulkLessonStatus, ...coursePatch } = action.payload;

        return applyBulkLessonStatusToCourseLikeState(
          state,
          coursePatch,
          bulkLessonStatus,
        );
      },
    );

    builder.addCase(removeLessonById.fulfilled, (state: any, action: any) => {
      if (!state || !state.structure) {
        return state;
      }

      if (state.structure === TRAINING_STRUCTURES.MODULES) {
        return {
          ...state,
          modules: state.modules.map((module: any) => ({
            ...module,
            lessons: module.lessons.filter(
              (lesson: any) => lesson.id !== action.payload.id,
            ),
          })),
        };
      } else {
        return {
          ...state,
          lessons: state.lessons.filter(
            (lesson: any) => lesson.id !== action.payload.id,
          ),
        };
      }
    });

    builder.addCase(
      updateLessonStatusById.fulfilled,
      (state: any, action: any) => {
        if (!state || !state.structure) {
          return state;
        }

        if (state.structure === TRAINING_STRUCTURES.MODULES) {
          return {
            ...state,
            modules: state.modules.map((module: any) => ({
              ...module,
              lessons: module.lessons.map((lesson: any) => {
                if (lesson.id === action.payload.id) {
                  return {
                    ...lesson,
                    status: action.payload.status,
                  };
                }

                return lesson;
              }),
            })),
          };
        } else {
          return {
            ...state,
            lessons: state.lessons.map((lesson: any) => {
              if (lesson.id === action.payload.id) {
                return {
                  ...lesson,
                  status: action.payload.status,
                };
              }

              return lesson;
            }),
          };
        }
      },
    );

    builder.addCase(
      reorderTrainingLessons.fulfilled,
      (state: any, action: any) => {
        const { moduleId, lessons } = action.payload;

        return {
          ...state,
          modules: state.modules.map((module: any) => {
            if (module.id === moduleId) {
              return {
                ...module,
                lessons,
              };
            }

            return module;
          }),
        };
      },
    );

    builder.addCase(
      reorderTrainingModules.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: _orderBy(
            state.modules.map((currentModule: any) => {
              const nextModule = action.payload.find(
                (module: any) => module.id === currentModule.id,
              );

              return {
                ...nextModule,
                lessons: currentModule.lessons,
              };
            }),
            ['order'],
            ['asc'],
          ),
        };
      },
    );
  },
});

export default trainingSlice.reducer;
